/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';

import type { FetchResult } from 'apollo-link';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';

import type {
	useChangeCompanyHubPublishStateChangeHubPublishStateMutation as useChangeCompanyHubPublishStateChangeHubPublishStateMutation$data,
	useChangeCompanyHubPublishStateChangeHubPublishStateMutationVariables as useChangeCompanyHubPublishStateChangeHubPublishStateMutation$variables,
} from './__types__/useChangeCompanyHubPublishStateChangeHubPublishStateMutation';
import type { ChangeHubPublishStateMutation as ChangeHubPublishStateMutationType } from './__types__/ChangeHubPublishStateMutation';
import { GraphQLFrontCoverState } from './__types__/ChangeHubPublishStateMutation';
import { ChangeHubPublishStateWriteQuery } from './ChangeHubPublishStateWriteQuery.graphql';

type useUpdateCompanyHubPublishStateResult = {
	data: ChangeHubPublishStateMutationType | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	changeHubPublishState: (
		frontCoverState: GraphQLFrontCoverState,
	) => Promise<FetchResult<ChangeHubPublishStateMutationType>>;
};

export const useChangeCompanyHubPublishState = (): useUpdateCompanyHubPublishStateResult => {
	const [updateSiteLookAndFeel, { data, loading, error }] = useMutation<
		useChangeCompanyHubPublishStateChangeHubPublishStateMutation$data,
		useChangeCompanyHubPublishStateChangeHubPublishStateMutation$variables
	>(
		graphql`
			mutation useChangeCompanyHubPublishStateChangeHubPublishStateMutation(
				$input: UpdateSiteLookAndFeelInput!
			) {
				updateSiteLookAndFeel(input: $input) {
					siteLookAndFeel {
						frontCoverState
					}
					success
					errors {
						message
						extensions {
							errorType
							statusCode
						}
					}
				}
			}
		`,
		{
			update: (cache, { data }) => {
				cache.writeQuery({
					query: ChangeHubPublishStateWriteQuery,
					data: {
						siteSettings: {
							// Codegen is specifically configured to not add __typename when generating the types file,
							// so unfortunately ts-ignore is required here.
							// @ts-ignore
							__typename: 'SiteSettings',
							frontCover: {
								// Codegen is specifically configured to not add __typename when generating the types file,
								// so unfortunately ts-ignore is required here.
								// @ts-ignore
								__typename: 'FrontCover',
								frontCoverState: data?.updateSiteLookAndFeel.siteLookAndFeel?.frontCoverState,
							},
						},
					},
				});
			},
		},
	);

	const changeHubPublishState = (frontCoverState: GraphQLFrontCoverState) =>
		updateSiteLookAndFeel({
			variables: {
				input: {
					faviconFiles: [],
					frontCoverState,
					showFrontCover: frontCoverState === GraphQLFrontCoverState.SHOWN,
				},
			},
		});

	return {
		data,
		loading,
		error,
		changeHubPublishState,
	};
};
